import React, { useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import moment from 'moment'
import TabMenu from '../../navigation/TabMenu'
import SettingTabsInfo from '../../../constants/SettingTabsInfo'
import { SETTINGS_BIZ_ACCESS_LOGS } from '../../../constants/Paths'
import { useAppSelector } from '../../../hooks'
import { itemIcon } from '../../../icons'
import { updateBizAccessLogComment } from '../../../modules/BizAccessLog'
import { getErrorString } from '../../../utils/errorUtils'
import './SettingsBizAccessLogs.css'

type LocationState = {
  log?: IBizAccessLog
  groupId?: GroupId
}

type HttpError = Error & { status?: number }

const EVENT_TYPE_LABELS: Record<string, string> = {
  login: 'ログイン',
  api_access: 'APIアクセス',
  qr_tag_view: 'QRタグ閲覧',
}

const formatEventType = (eventType?: string | null): string => (
  eventType ? (EVENT_TYPE_LABELS[eventType] ?? eventType) : '-'
)

const formatLoggedAt = (value?: string | null): string => {
  if (!value) return '-'
  return moment(value).utcOffset(540).format('YYYY/MM/DD HH:mm')
}

const buildUserLabel = (log: IBizAccessLog): { name: string; email?: string | null } => {
  if (log.user) {
    return {
      name: log.user.name || log.user.email || `ID: ${log.user.id}`,
      email: log.user.email,
    }
  }
  if (log.user_id) {
    return { name: `ID: ${log.user_id}` }
  }
  return { name: '-' }
}

type MamorioSummary = Pick<IMamorio, 'id' | 'name' | 'image' | 'user'>

const buildMamorioLabel = (mamorio?: MamorioSummary | null): {
  name: string
  ownerName: string
  imageUrl: string
} => {
  if (!mamorio) {
    return { name: '-', ownerName: '-', imageUrl: itemIcon }
  }
  const ownerName = mamorio.user?.name
    || mamorio.user?.email
    || (mamorio.user?.id ? `ID: ${mamorio.user.id}` : '-')
  const imageUrl = mamorio.image?.normal?.url
    ?? mamorio.image?.url
    ?? mamorio.image?.retina?.url
    ?? itemIcon
  return {
    name: mamorio.name || `ID: ${mamorio.id}`,
    ownerName,
    imageUrl,
  }
}

const parseMamorioIdFromPath = (path?: string | null): number | null => {
  if (!path) return null
  const match = path.match(/\/(?:group_mamorios|team_mamorios)\/(\d+)/)
  if (!match) return null
  const parsed = Number(match[1])
  return Number.isNaN(parsed) ? null : parsed
}

const isForbiddenError = (error: unknown): boolean => (
  error instanceof Error && (error as HttpError).status === 403
)

const SettingsBizAccessLogDetail = () => {
  const history = useHistory()
  const location = useLocation<LocationState>()
  const { logId } = useParams<{ logId: string }>()
  const team = useAppSelector((state) => state.team)
  const mamorios = useAppSelector((state) => state.mamorio.mamorios) ?? []
  const tabsInfo = useMemo(
    () => SettingTabsInfo({
      groups: team.groups ?? null,
      deviceEnabled: team.device_enabled,
      qrTagEnabled: team.qr_tag_enabled ?? false,
      rfidTagEnabled: team.rfid_tag_enabled ?? false,
      eventNotificationSettingsEnable: team.event_notification_settings_enable ?? false,
    }),
    [
      team.device_enabled,
      team.event_notification_settings_enable,
      team.groups,
      team.qr_tag_enabled,
      team.rfid_tag_enabled,
    ],
  )

  const [log, setLog] = useState<IBizAccessLog | null>(location.state?.log ?? null)
  const [commentDraft, setCommentDraft] = useState(log?.comment ?? '')
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const groupId = location.state?.groupId ?? null

  const userLabel = log ? buildUserLabel(log) : { name: '-' }
  const mamorioId = log?.target_id ?? parseMamorioIdFromPath(log?.path)
  const mamorioFromStore = mamorioId
    ? mamorios.find((mamorio) => mamorio.id === mamorioId) ?? null
    : null
  const mamorioLabel = log
    ? buildMamorioLabel(log.target_mamorio ?? mamorioFromStore)
    : { name: '-', ownerName: '-', imageUrl: itemIcon }
  const hasMamorio = Boolean(log?.target_mamorio ?? mamorioFromStore)
  const canSave = log ? commentDraft !== (log.comment ?? '') : false

  const handleBack = () => {
    history.push(SETTINGS_BIZ_ACCESS_LOGS)
  }

  const handleStartEdit = () => {
    if (!log) return
    setCommentDraft(log.comment ?? '')
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!log || !groupId) return
    setSaving(true)
    setError(null)
    setForbidden(false)

    try {
      const updated = await updateBizAccessLogComment(groupId, log.id, commentDraft)
      setLog(updated)
      setIsEditing(false)
    } catch (err) {
      if (isForbiddenError(err)) {
        setForbidden(true)
      } else {
        setError(getErrorString(err))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <TabMenu tabsInfo={tabsInfo}>
      <div className="biz-access-logs">
        <div className="biz-access-logs__detail-header">
          <div>
            <h2 className="biz-access-logs__title">Bizアクセスログ 詳細</h2>
            <p className="biz-access-logs__description">
              一覧から選択したログの詳細を確認できます。
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleBack}
          >
            一覧へ戻る
          </button>
        </div>

        {!log || !groupId ? (
          <div className="biz-access-logs__detail-card">
            <p className="biz-access-logs__empty">
              ログ情報が取得できませんでした。一覧から開き直してください。
            </p>
            {logId && (
              <p className="biz-access-logs__detail-id">
                log_id:
                {' '}
                {logId}
              </p>
            )}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleBack}
            >
              一覧へ戻る
            </button>
          </div>
        ) : (
          <>
            <div className="biz-access-logs__detail-card">
              <dl className="biz-access-logs__detail-grid">
                <dt>logged_at</dt>
                <dd>{formatLoggedAt(log.logged_at)}</dd>
                <dt>user</dt>
                <dd>
                  <div className="biz-access-logs__user-name">{userLabel.name}</div>
                  {userLabel.email && (
                    <div className="biz-access-logs__user-email">{userLabel.email}</div>
                  )}
                </dd>
                <dt>event_type</dt>
                <dd>{formatEventType(log.event_type)}</dd>
                <dt>mamorio</dt>
                <dd>
                  {hasMamorio ? (
                    <div className="biz-access-logs__mamorio">
                      <img
                        src={mamorioLabel.imageUrl}
                        alt={mamorioLabel.name}
                        className="biz-access-logs__mamorio-image"
                        onError={(event) => {
                          const target = event.currentTarget
                          target.src = itemIcon
                        }}
                      />
                      <div>
                        <div className="biz-access-logs__mamorio-name">{mamorioLabel.name}</div>
                        <div className="biz-access-logs__mamorio-owner">
                          所持者:
                          {' '}
                          {mamorioLabel.ownerName}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="biz-access-logs__placeholder">-</span>
                  )}
                </dd>
                <dt>path</dt>
                <dd>
                  {log.request_method && (
                    <span className="biz-access-logs__method">
                      {log.request_method}
                    </span>
                  )}
                  <span className="biz-access-logs__path">{log.path ?? '-'}</span>
                </dd>
                <dt>ip_address</dt>
                <dd>{log.ip_address ?? '-'}</dd>
                <dt>user_agent</dt>
                <dd>{log.user_agent ?? '-'}</dd>
              </dl>
            </div>

            <div className="biz-access-logs__detail-card">
              <div className="biz-access-logs__detail-comment-header">
                <h3 className="biz-access-logs__detail-title">comment</h3>
                {!isEditing && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleStartEdit}
                  >
                    編集
                  </button>
                )}
              </div>

              {forbidden && (
                <p className="biz-access-logs__error">
                  このグループではBizアクセスログを利用できません。
                </p>
              )}

              {!forbidden && error && (
                <p className="biz-access-logs__error">{error}</p>
              )}

              {isEditing ? (
                <div className="biz-access-logs__comment">
                  <textarea
                    value={commentDraft}
                    onChange={(event) => setCommentDraft(event.target.value)}
                    rows={4}
                    placeholder="コメントを入力"
                  />
                  <div className="biz-access-logs__comment-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSave}
                      disabled={saving || !canSave}
                    >
                      保存
                    </button>
                  </div>
                </div>
              ) : (
                <p className="biz-access-logs__comment-text">
                  {log.comment?.trim() ? log.comment : '（未入力）'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </TabMenu>
  )
}

export default SettingsBizAccessLogDetail
